import { useState } from 'react';
import {
  graphql, fetchQuery, useRelayEnvironment, useFragment,
} from 'react-relay';
import { nanoid } from 'nanoid';
import { useTranslation, lang } from '@/helpers/translate';
import Icon from '@/core/icon.js';
import Avatar from '@/core/avatar.js';
import Loading from '@/core/loading';

const queryMoreReviews = graphql`
  query reviewsLoadMoreQuery($spaceRef: String!, $lang: String) {
    item : getSpaceByRef(ref: $spaceRef) {
      reviews {
        type
        rate
        recommend
        comment(lang: $lang)
        createdAt
        rider {
          ...avatar_user
          firstname
          company {
            name
          }
        }
      }
    }
  }
`;
const queryReviews = graphql`
  fragment reviews_reviews on Review
  @relay(plural: true)
  @argumentDefinitions(
    lang : {type:"String!"}
  ) {
    type
    rate
    recommend
    comment(lang: $lang)
    createdAt
    rider {
      ...avatar_user
      firstname
      company {
        name
      }
    }
  }
`;

const rateComponent = {};

const hocRate = (rate) => {
  if (!rateComponent[`component${rate}`]) {
    rateComponent[`component${rate}`] = (props) => [...Array(rate).keys()].map((ra) => ({ ...ra, reactKeyId: nanoid() })).map((ra) => <Icon key={`spacestaticreviewiconstar${ra?.reactKeyId}`} name="star" className="w-3 h-3 mr-1 fill-current text-purple" />);
  }

  return rateComponent[`component${rate}`];
};
export default function Reviews({
  reviews, rate, reviewCounter, spaceRef,
}) {
  const { t } = useTranslation();
  const limitDefault = 10;
  const [limit, setLimit] = useState(limitDefault);
  const [currentReviews, setCurrentReviews] = useState(reviews);
  const environment = useRelayEnvironment();
  const [isLoading, setLoading] = useState(false);
  const langCurrent = lang();
  if (!currentReviews || !reviewCounter) return null;

  function loadMoreReviews() {
    if (currentReviews.length < reviewCounter) {
      setLoading(true);
      fetchQuery(environment, queryMoreReviews, {
        spaceRef,
        lang: langCurrent,
      }).subscribe({
        next: (dataRes) => {
          setCurrentReviews(dataRes?.item?.reviews || []);
          setLimit(limit === limitDefault ? reviewCounter + 1 : limitDefault);
          setLoading(false);
        },
        error: (error1) => {
          console.log('loadMoreReviews', error1);
        },
      });
    } else {
      setLimit(limit === limitDefault ? reviewCounter + 1 : limitDefault);
    }
  }

  return (
    <section className="flex-col pt-6 border-t text-purple-5/50 md:flex-row border-white-dark2 md:pt-20">
      {/* <h2 className="w-full pt-8 pb-2 text-xl text-black md:pt-12" id="id_reviews">{t('space:reviews')}</h2> */}
      <h2 className="flex items-center w-full pt-6 pb-2 text-lg lg:text-xl md:pt-12 text-purple-5" id="id_reviews">
        <Icon name="star" className="w-5 h-5 fill-current text-purple" />
        <span className="ml-2 text-xl font-medium text-purple">{rate}</span>
        <span className="mx-2">·</span>
        <span className="text-xl font-medium text-black">
          {reviewCounter <= 1 ?
            t('review:count_review', {
              count: reviewCounter || 0,
            }) :
            t('review:count_review_plural', {
              count: reviewCounter || 0,
            })
          }
        </span>
      </h2>
      <div className={`grid grid-cols-1 lg:grid-cols-2 md:gap-y-0 lg:gap-x-10 xl:gap-x-24 ${limit === limitDefault ? 'transition' : 'transition'}`}>
        {currentReviews.slice(0, limit).map((r) => ({ ...r, reactKeyId: nanoid() })).map((r) => {
          const RateComponent = hocRate(r.rate);
          return (
          <div className="flex flex-col py-4 lg:py-8 md:flex-row" key={`spacestaticreviewdiv${r?.reactKeyId}`} itemProp="review" itemScope itemType="http://schema.org/Review">
            <Avatar user={r.rider || null} className="relative flex-none mr-5 overflow-hidden rounded-lg w-14 h-14 lg:w-16 lg:h-16" bgColor="bg-purple-2/80" />
            <div className="flex-grow-0">
              <div className="flex flex-row items-center my-1 lg:mb-2 md:mt-0">
                <span className="mr-3 font-medium text-black">{r?.rider?.firstname}</span>
                <div className="flex items-center" itemProp="reviewRating" itemScope itemType="http://schema.org/Rating">
                  <meta className="hidden" itemProp="ratingValue" content={r.rate} />
                  <RateComponent />
                </div>
              </div>
              <div className="flex items-center mt-1 space-x-1 lg:mt-2" itemProp="author" itemScope itemType="http://schema.org/Person">
                <meta itemProp="name" content={r?.rider?.firstname} />
                <meta itemProp="sameAs" content={r?.rider?.company?.name} />
                {!r?.rider?.company?.name || <span className="flex-shrink px-2 py-0.5 text-sm truncate rounded max-w-180 bg-purple/10 text-purple">{r?.rider?.company?.name}</span>}
              </div>
              <p className="pt-2 lg:pt-3 text-purple-5/80" itemProp="reviewBody">{r.comment}</p>
            </div>
          </div>
        );
})}
      </div>
      {reviewCounter < limitDefault || (
      <button
        className="px-3 py-2 rounded-lg cursor-pointer text-purple-5 bg-purple-0 hover:bg-purple/10"
        onClick={() => {
          loadMoreReviews();
        }}
      >
        {isLoading ? <Loading /> : t(limit === limitDefault ? 'btn:see_more_reviews' : 'btn:see_less')}
      </button>
      )}
    </section>
  );
}
